import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ProphetsBenefits — Section `#utilities` (Holder Utilities).
 *
 * Three-card composition: Revenue Share, Ecosystem Access, Holder Rewards.
 * All cards share the same premium metallic frame + scroll-reveal.
 */
const BENEFITS = [
  {
    id: "revenue",
    title: "Revenue Share",
    body: "Holders receive a share of the revenue generated across every game created within the Prophets ecosystem.",
    accent: "#e05a5a",
    glow: "rgba(224, 90, 90, 0.35)",
  },
  {
    id: "access",
    title: "Ecosystem Access",
    body: "Gain privileged access to the Prophets ecosystem, including future launches, testing phases, whitelists, exclusive experiences, and new opportunities.",
    accent: "#e08a3a",
    glow: "rgba(224, 138, 58, 0.35)",
  },
  {
    id: "ingame",
    title: "Holder Rewards",
    body: "Unlock exclusive in-game advantages, rewards, prizes, giveaways, community activations, and holder-only opportunities across the Prophets ecosystem.",
    accent: "#4fc4d9",
    glow: "rgba(79, 196, 217, 0.35)",
  },
];

function BenefitIcon({ id }) {
  const paths = {
    revenue:    "M4 12h16M12 4v16M6 8l12 8M18 8L6 16",
    access:     "M12 3l9 5v8l-9 5-9-5V8l9-5zM12 3v18",
    ingame:     "M8 12l4-4 4 4-4 4-4-4zM3 12h4M17 12h4M12 3v4M12 17v4",
  };
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="prophets-benefit-icon" aria-hidden="true">
      <path d={paths[id]} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BenefitCard({ b, index }) {
  return (
    <article
      data-testid={`benefit-card-${b.id}`}
      className={`prophets-benefit-card ${b.featured ? "prophets-benefit-card--featured" : ""}`}
      style={{
        "--reveal-delay": `${index * 90}ms`,
        "--benefit-accent": b.accent,
        "--benefit-glow": b.glow,
      }}
    >
      <span aria-hidden="true" className="prophets-benefit-corner prophets-benefit-corner--tl" />
      <span aria-hidden="true" className="prophets-benefit-corner prophets-benefit-corner--tr" />
      <span aria-hidden="true" className="prophets-benefit-corner prophets-benefit-corner--bl" />
      <span aria-hidden="true" className="prophets-benefit-corner prophets-benefit-corner--br" />

      <header className="prophets-benefit-head">
        <span className="prophets-benefit-icon-wrap"><BenefitIcon id={b.id} /></span>
        <span className="p-mono prophets-benefit-index">{String(index + 1).padStart(2, "0")}</span>
      </header>

      <h3 className="p-nav prophets-benefit-title">{b.title.toUpperCase()}</h3>
      <p className="prophets-benefit-body">{b.body}</p>

      <span aria-hidden="true" className="prophets-benefit-sweep" />
    </article>
  );
}

export function ProphetsBenefits() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [active, setActive] = useState(0);
  const dragRef = useRef({ startX: 0, startY: 0, dragging: false, dx: 0, dy: 0, moved: false });

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Track mobile viewport so the carousel logic only mounts <768px.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const total = BENEFITS.length;
  const clamp = useCallback((i) => Math.max(0, Math.min(total - 1, i)), [total]);
  const go = useCallback((d) => setActive((a) => clamp(a + d)), [clamp, total]);

  // Pointer-based swipe. Only triggers a slide when horizontal drag is
  // clearly dominant AND large enough — tapping the card or scrolling
  // the page vertically will NEVER trigger navigation.
  const H_THRESHOLD = 55;
  const onDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, dragging: true, dx: 0, dy: 0, moved: false };
  };
  const onMove = (e) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dx = e.clientX - dragRef.current.startX;
    dragRef.current.dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dragRef.current.dx) > 8 || Math.abs(dragRef.current.dy) > 8) {
      dragRef.current.moved = true;
    }
  };
  const onUp = () => {
    const { dx, dy, dragging } = dragRef.current;
    dragRef.current.dragging = false;
    if (!dragging) return;
    if (Math.abs(dx) > H_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.4) {
      go(dx < 0 ? +1 : -1);
    }
  };

  return (
    <section
      id="utilities"
      ref={ref}
      data-testid="utilities-section"
      className={`prophets-benefits ${inView ? "is-in-view" : ""} relative w-full px-5 md:px-10 py-20 md:py-28`}
    >
      <header className="relative z-10 mx-auto max-w-[1360px] w-full mb-12 md:mb-16 text-center">
        <h2 data-testid="utilities-title" className="p-nav prophets-benefits-title">
          Holder Benefits
        </h2>
        <p className="prophets-benefits-lede">
          More than ownership. A permanent position inside the Prophets ecosystem.
        </p>
      </header>

      <div className="relative z-10 mx-auto max-w-[1200px] w-full">
        {/* Mobile: single-card carousel (only rendered <768px). Interactions
            are limited to arrows / dots / horizontal swipe — tapping the
            card itself never advances the slide. */}
        {isMobile && (
          <div className="prophets-benefits-mobile" data-testid="benefits-mobile-carousel">
            <button
              type="button"
              aria-label="Previous benefit"
              data-testid="benefits-mobile-prev"
              onClick={() => go(-1)}
              disabled={active === 0}
              className={`prophets-benefits-arrow prophets-benefits-arrow--left ${active === 0 ? "is-disabled" : ""}`}
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              aria-label="Next benefit"
              data-testid="benefits-mobile-next"
              onClick={() => go(+1)}
              disabled={active === total - 1}
              className={`prophets-benefits-arrow prophets-benefits-arrow--right ${active === total - 1 ? "is-disabled" : ""}`}
            >
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <div
              className="prophets-benefits-mobile-viewport"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
            >
              <div
                className="prophets-benefits-mobile-track"
                style={{ transform: `translateX(-${active * 100}%)` }}
              >
                {BENEFITS.map((b, i) => (
                  <div key={b.id} className="prophets-benefits-mobile-slide">
                    <BenefitCard b={b} index={i} />
                  </div>
                ))}
              </div>
            </div>
            <div className="prophets-benefits-dots" role="tablist" aria-label="Benefit pagination">
              {BENEFITS.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  aria-label={`Show benefit ${i + 1} of ${total}`}
                  data-testid={`benefits-mobile-dot-${i}`}
                  onClick={() => setActive(i)}
                  className={`prophets-benefits-dot ${i === active ? "is-active" : ""}`}
                />
              ))}
            </div>
          </div>
        )}
        {/* Desktop: 3 equal-width cards in one row (hidden on mobile via CSS). */}
        <div className="prophets-benefits-desktop">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {BENEFITS.map((b, i) => (
              <BenefitCard key={b.id} b={b} index={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
